import React, { useCallback } from 'react'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import Text from 'core/elements/Text'
import useUpdateAction from 'core/hooks/useUpdateAction'
import ModalForm from 'core/elements/modal/ModalForm'
import { updateVmMetadata } from './actions'
import Info from 'core/components/validatedForm/Info'
import { getCurrentUser } from 'openstack/helpers'
import { hasOtherUserVm } from './helpers'
import PicklistField from 'core/components/validatedForm/DropdownField'
import useParams from 'core/hooks/useParams'
import MigrationPriorityPicklist from './MigrationPriorityPicklist'
import ExternalLink from 'core/components/ExternalLink'
import { drrDoc } from 'app/docLinks'

const useStyles = makeStyles<Theme>((theme) => ({
  fields: {
    gap: 16,
    display: 'grid',
    paddingBottom: 48,
  },
}))

export default function UpdateMigrationPriorityDialog({ rows: vms, onClose }) {
  const vm = vms?.[0]
  const classes = useStyles()

  const defaultParams = {
    priority: vm?.metadata?.['migration-priority'] || '',
  }
  const { params, getParamsUpdater, setParams, updateParams } = useParams(defaultParams)

  const { role: userRole, userId } = getCurrentUser()
  const { update, updating, error, reset } = useUpdateAction(updateVmMetadata)

  const handleSubmit = useCallback(async () => {
    const metadata = {
      ...vm?.metadata,
      'migration-priority': params.priority,
    }
    const body = {
      metadata,
    }
    const { success } = await update({ id: vm?.id, body })
    if (success) {
      handleClose(true)
    }
  }, [vm, params])

  const handleClose = (success?: boolean) => {
    reset()
    onClose(success)
  }

  return (
    <ModalForm
      title="Set Migration Priority"
      onSubmit={handleSubmit}
      onClose={handleClose}
      loading={false}
      submitting={updating}
      error={error}
      submitTitle="Set Priority"
      panel="dialog"
      open
    >
      <div className={classes.fields}>
        {userRole === 'member' && hasOtherUserVm({ vms, userId }) && (
          <Info error>NOTE: You are performing an operation on another user's VM.</Info>
        )}
        <Text variant="body2">
          Assign a migration priority to the selected virtual machine. The migration priority
          determines whether the virtual machine qualifies as a candidate for migration by the DRR
          service, and, if it does, specifies the priority level for its migration.{' '}
          <ExternalLink url={drrDoc}>More info</ExternalLink>
        </Text>
        <Text variant="body2">
          VM Name: <strong>{vm?.name || vm?.id}</strong>
        </Text>
        <PicklistField
          id="priority"
          DropdownComponent={MigrationPriorityPicklist}
          value={params.priority}
          onChange={getParamsUpdater('priority')}
        />
      </div>
    </ModalForm>
  )
}
